import React from "react";
import clsx from "clsx";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "accent" | "navy" | "secondary" | "outline" | "success" | "danger" | "ghost" | "active";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variants = {
  primary:
    "bg-[var(--primary)] text-white hover:bg-[var(--hoverBtnBgColor)] shadow-sm focus-visible:ring-[var(--primary)]",
  // Orange call-to-action — the source app's primary action colour.
  accent:
    "bg-[var(--actionOrange)] text-white hover:bg-[var(--actionOrangeHover)] shadow-sm focus-visible:ring-[var(--actionOrange)]",
  // Royal-blue secondary action (Export etc.) — the source app's
  // --secondaryActionBlue.
  navy:
    "bg-[var(--secondaryActionBlue)] text-white hover:bg-[var(--secondaryActionBlueHover)] shadow-sm focus-visible:ring-[var(--secondaryActionBlue)]",
  secondary:
    "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-400",
  outline:
"bg-white border border-slate-300 text-slate-700 hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--primary)]/5 focus-visible:ring-2 focus-visible:ring-[var(--primary)] transition-colors",
  success: "bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500",
  danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",
  ghost: "bg-transparent text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-400",
  active:
  "bg-[var(--btnActiveBg)] text-[var(--btnActiveText)] border border-[rgb(var(--primaryRgb)/0.15)] hover:bg-[rgb(var(--primaryRgb)/0.1)]"
};

const sizes = {
  sm: "h-8 px-3 text-xs rounded-md gap-1.5",
  md: "h-10 px-4 text-sm rounded-lg gap-2",
  lg: "h-12 px-6 text-base rounded-lg gap-2.5",
};

export default function Button({
  children,
  variant = "primary",
  size = "md",
  loading,
  leftIcon,
  rightIcon,
  className,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={clsx(
        // Base Layout
        "inline-flex items-center justify-center font-medium transition-colors duration-150 relative",
        "select-none touch-manipulation",
        
        // Focus States (Enterprise Standard)
        "focus:outline-none",
        "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
        
        // Disabled States
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
        
        // Dynamic Variants & Sizes
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <svg 
          className={clsx(
            "animate-spin shrink-0",
            size === "lg" ? "h-5 w-5" : "h-4 w-4",
            variant === "secondary" || variant === "ghost" ? "text-slate-500" : "text-current"
          )} 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : (
        leftIcon
      )}

      {/* Elegant text handling for loading state */}
      <span className={clsx("inline-flex items-center", loading && "opacity-90")}>
        {children}
      </span>

      {!loading && rightIcon}
    </button>
  );
}